import React, { useCallback, useMemo, useState } from 'react';
import s from './Accordion.scss';
import { AccordionContext, AccordionContextProps } from './AccordionContext';
import { cn } from '@express-labs/raven-tools';

type CommonProps = {
  children: React.ReactNode;
  /** An optional class name to customize the div wrapping the accordion */
  className?: string;
};

type WithKeepIdxOpen = {
  /** The id of an accordion item that you wish to initially display in the open state */
  openIdx: string;
  /** Keep the index in openIdx open.  The user cannot collapse the openIdx accordion item. */
  keepIdxOpen: boolean;
};

type WithoutKeepIdxOpen = {
  /** The id of an accordion item that you wish to initially display in the open state */
  openIdx?: string;
  /** Keep the index in openIdx open.  The user cannot collapse the openIdx accordion item. */
  keepIdxOpen?: never;
};

export type AccordionProps =
  | (CommonProps & WithKeepIdxOpen)
  | (CommonProps & WithoutKeepIdxOpen);

const Accordion = ({
  children,
  openIdx,
  keepIdxOpen,
  className,
}: AccordionProps) => {
  const initialState: Record<string, boolean> = {};

  if ((keepIdxOpen && openIdx) || openIdx) {
    initialState[openIdx] = true;
  }
  const [expansion, setExpansion] = useState(initialState);

  const toggleAccordion = useCallback(
    (dataAccordionId: string) => {
      if (keepIdxOpen && openIdx === dataAccordionId) {
        return;
      }

      const updatedExpansion = { ...expansion };
      updatedExpansion[dataAccordionId] = !expansion[dataAccordionId];

      setExpansion(updatedExpansion);
    },
    [expansion, keepIdxOpen, openIdx]
  );

  const value = useMemo<AccordionContextProps>(
    () => ({
      expansion,
      toggleExpansion: toggleAccordion,
    }),
    [expansion, toggleAccordion]
  );

  return (
    <AccordionContext.Provider value={value}>
      <div className={cn(s.accordion, className)}>{children}</div>
    </AccordionContext.Provider>
  );
};

Accordion.displayName = 'Accordion';

export default Accordion;
